/*
 * Words sent to Artwork (cross-app pages, 29 Oct 2026: "notes → artwork: a text block → a text layer"): a text parcel
 * (`type: 'text'`, items `{ name?, text }`) read as a layered file, so it lands the way a layered picture does — a new
 * artboard in a new file or one of yours (LayersImport), every piece of words its own live text layer, top to bottom
 * on a white page. A text that runs over several lines is a wrapped box (`wrap: true` and its width), its height
 * worked out from its lines so the next piece sits under it.
 */

export const TEXT_PAGE = { w: 1080, margin: 80, gap: 48, maxH: 4096 } as const;
const BODY = { size: 40, lineHeight: 1.35, weight: 500 } as const;
const HEAD = { size: 64, lineHeight: 1.15, weight: 700 } as const;
/** About how many characters fit on a line of `width` at `size` (a typical face is about half its size wide). */
const perLine = (width: number, size: number) => Math.max(8, Math.floor(width / (size * 0.52)));

/** How many lines `text` takes at `size` in a box `width` wide: each paragraph wraps on its own. */
export function linesOf(text: string, width: number, size: number): number {
  const n = perLine(width, size);
  return String(text).split(/\n/).reduce((sum, para) => sum + Math.max(1, Math.ceil(para.trim().length / n)), 0);
}

interface TextItem { name?: unknown; text?: unknown }

/**
 * The flobi.layers file for a text parcel (its raw JSON), or null when it holds no words. A piece with its own name
 * (a heading in the doc) gets the name as a bold line above it; one named like the parcel does not repeat it.
 */
export function textLayerFile(parcel: { name?: unknown; items?: unknown }): Record<string, unknown> | null {
  const items = (Array.isArray(parcel?.items) ? (parcel.items as TextItem[]) : []).filter((it) => typeof it?.text === 'string' && it.text.trim());
  if (!items.length) return null;
  const name = String(parcel?.name || '').trim() || 'Words';
  const width = TEXT_PAGE.w - TEXT_PAGE.margin * 2;
  const layers: Record<string, unknown>[] = [];
  let y = TEXT_PAGE.margin;
  const add = (text: string, font: typeof BODY | typeof HEAD, label: string) => {
    const h = Math.ceil(linesOf(text, width, font.size) * font.size * font.lineHeight);
    layers.push({
      id: `text-${layers.length + 1}`,
      kind: 'text',
      name: label.slice(0, 200),
      bbox: { x: TEXT_PAGE.margin, y, w: width, h },
      z: layers.length + 1,
      wrap: true,
      text,
      style: { fontFamily: 'Inter', fontWeight: font.weight, fontSizePx: font.size, color: '#111111', letterSpacingPx: 0, lineHeight: font.lineHeight, align: 'left', alignY: 'top' },
    });
    y += h;
  };
  items.forEach((it, i) => {
    const words = String(it.text).trim();
    const own = typeof it.name === 'string' ? it.name.trim() : '';
    if (own && own !== name && !words.startsWith(own)) {
      add(own, HEAD, own);
      y += Math.round(HEAD.size * 0.4);
    }
    add(words, BODY, own || (words.split(/\n/)[0] ?? '').slice(0, 60) || `Text ${i + 1}`);
    y += TEXT_PAGE.gap;
  });
  const h = Math.min(TEXT_PAGE.maxH, Math.max(TEXT_PAGE.w, Math.ceil(y - TEXT_PAGE.gap + TEXT_PAGE.margin)));
  layers.unshift({ id: 'page', kind: 'fill', name: 'Page', fill: '#ffffff', bbox: { x: 0, y: 0, w: TEXT_PAGE.w, h }, z: 0 });
  return { kind: 'flobi.layers', version: 1, name, model: 'layers', editableText: true, canvas: { w: TEXT_PAGE.w, h }, size: { width: TEXT_PAGE.w, height: h }, fonts: [], layers, pictures: [], preview: null };
}
